import React from "react";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

// Grouped bar chart drawn as inline vector markup, used by OverviewScreen
// (daily/weekly activity) and DrilldownDialog (activity by member). Bars come
// in pairs; the per-bar `spacing` is the gap AFTER that bar (pair members
// separated by a small spacing, groups by a larger one).
//
// Geometry (matches gifted-charts-core's BarChart/index.js + RenderBars, the
// reference this was measured against):
//  - maxValue: props.maxValue ?? (props.stepValue ? stepValue*noOfSections :
//    roundUpToNext10(maxDataValue)) || 10.
//  - stepValue = props.stepValue ?? maxValue/noOfSections.
//  - stepHeight = height/noOfSections. Bars are bottom-anchored on the baseline.
//  - barLeft(k) = yAxisLabelWidth + initialSpacing + Σ_{i<k}(barWidth + spacing_i).
//    (yAxisLabelWidth baked in so callers' absolutely-positioned hover zones,
//    which use x = yAxisLabelWidth + initialSpacing + group*pitch, line up.)
//  - barHeight(k) = value * height / maxValue - xAxisThickness(1), clamped ≥ 0.
//  - Top corners rounded (barBorderTopLeft/RightRadius); bottom square → a
//    rounded-top <path>, since a plain rect rx rounds all four corners.
//  - Gradient (showGradient): DIAGONAL (0,0)→(1,1), stop0 = gradientColor
//    (top-left), stop1 = frontColor (bottom-right). No gradient → solid frontColor.
//  - Dashed rules "dashWidth,dashGap" at y = i*stepHeight, i=0..noOfSections.
//  - X-axis labels centered at barLeft + (labelWidth ?? barWidth)/2.
// The chart is static: bars draw at full height with no enter animation.

export type BarChartDatum = Omittable<{
  value: number;
  frontColor: string;
  gradientColor?: string;
  showGradient?: boolean;
  label?: string;
  /** Gap AFTER this bar. Falls back to the chart-level `spacing`. */
  spacing?: number;
  labelWidth?: number;
  labelTextStyle?: { color?: string; fontSize?: number; textAlign?: "center"; width?: number };
  /** Passed straight back to onClick. */
  [key: string]: unknown;
}>;

export type GroupedBarChartProps = Omittable<{
  data: BarChartDatum[];
  barWidth: number;
  noOfSections: number;
  height: number;
  spacing: number;
  initialSpacing: number;
  endSpacing: number;
  width: number;
  yAxisLabelWidth: number;
  yAxisTextStyle: { color: string; fontSize: number };
  rulesColor: string;
  dashWidth: number;
  dashGap: number;
  barBorderTopLeftRadius: number;
  barBorderTopRightRadius: number;
  /** Explicit max — when omitted, auto (round data max up to next 10). */
  maxValue?: number;
  stepValue?: number;
  onClick?: (item: BarChartDatum) => void;
}>;

const X_AXIS_THICKNESS = 1;

function roundUpToNext10(v: number): number {
  const m = v % 10;
  return v + (10 - m);
}

// Rounded-top-only bar path (bottom corners square).
function barPath(
  left: number,
  top: number,
  w: number,
  h: number,
  rTL: number,
  rTR: number,
): string {
  const right = left + w;
  const bottom = top + h;
  // Clamp radii so they never exceed half the bar dimensions.
  const tl = Math.max(0, Math.min(rTL, w / 2, h));
  const tr = Math.max(0, Math.min(rTR, w / 2, h));
  return [
    `M ${left} ${bottom}`,
    `L ${left} ${top + tl}`,
    tl > 0 ? `Q ${left} ${top} ${left + tl} ${top}` : `L ${left} ${top}`,
    `L ${right - tr} ${top}`,
    tr > 0 ? `Q ${right} ${top} ${right} ${top + tr}` : `L ${right} ${top}`,
    `L ${right} ${bottom}`,
    "Z",
  ].join(" ");
}

export function GroupedBarChart({
  data,
  barWidth,
  noOfSections,
  height,
  spacing,
  initialSpacing,
  endSpacing,
  width,
  yAxisLabelWidth,
  yAxisTextStyle,
  rulesColor,
  dashWidth,
  dashGap,
  barBorderTopLeftRadius,
  barBorderTopRightRadius,
  maxValue: maxValueProp,
  stepValue: stepValueProp,
  onClick,
}: GroupedBarChartProps) {
  const rawMax = Math.max(0, ...data.map((d) => d.value));
  const maxValue =
    (maxValueProp ??
      (stepValueProp ? stepValueProp * noOfSections : roundUpToNext10(rawMax))) ||
    10;
  const stepValue = stepValueProp ?? maxValue / noOfSections;
  const stepHeight = height / noOfSections;

  // Left edge of each bar (yAxisLabelWidth baked into the coordinate space).
  const lefts: number[] = [];
  {
    let x = yAxisLabelWidth + initialSpacing;
    for (const d of data) {
      lefts.push(x);
      x += barWidth + (d.spacing ?? spacing);
    }
  }

  const heightFactor = height / maxValue;
  const getBarHeight = (value: number) =>
    Math.max(0, Math.abs(value) * heightFactor - X_AXIS_THICKNESS);

  const dashArray = `${dashWidth},${dashGap}`;
  const ruleX1 = yAxisLabelWidth;
  const ruleX2 = yAxisLabelWidth + width + endSpacing;

  const svgWidth = ruleX2;
  const labelBand = 6 + 18;
  const svgHeight = height + labelBand;

  return (
    <svg width={svgWidth} height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`}>
      <defs>
        {data.map((d, i) =>
          d.showGradient && d.gradientColor ? (
            <linearGradient key={`grad-${i}`} id={`bar-grad-${i}`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor={d.gradientColor} />
              <stop offset="1" stopColor={d.frontColor} />
            </linearGradient>
          ) : null,
        )}
      </defs>

      {/* Dashed grid rules */}
      {Array.from({ length: noOfSections + 1 }, (_, i) => {
        const y = stepHeight * i;
        return (
          <line
            key={`rule-${i}`}
            x1={ruleX1}
            y1={y}
            x2={ruleX2}
            y2={y}
            stroke={rulesColor}
            strokeWidth={1}
            strokeDasharray={dashArray}
          />
        );
      })}

      {/* Y-axis labels: value = maxValue - stepValue*i */}
      {Array.from({ length: noOfSections + 1 }, (_, i) => {
        const value = maxValue - stepValue * i;
        const y = stepHeight * i;
        return (
          <text
            key={`ylabel-${i}`}
            x={yAxisLabelWidth - 6}
            y={y + 3}
            fontSize={yAxisTextStyle.fontSize}
            fill={yAxisTextStyle.color}
            textAnchor="end"
          >
            {String(Math.round(value))}
          </text>
        );
      })}

      {/* Bars */}
      {data.map((d, i) => {
        const bh = getBarHeight(d.value);
        const top = height - bh;
        const left = at(lefts, i, "bar left edge");
        const fill =
          d.showGradient && d.gradientColor ? `url(#bar-grad-${i})` : d.frontColor;
        if (bh <= 0) return null;
        return (
          <path
            key={`bar-${i}`}
            d={barPath(left, top, barWidth, bh, barBorderTopLeftRadius, barBorderTopRightRadius)}
            fill={fill}
            onClick={onClick ? () => onClick(d) : undefined}
            cursor={onClick ? "pointer" : undefined}
          />
        );
      })}

      {/* X-axis labels centered on barLeft + labelWidth/2 */}
      {data.map((d, i) =>
        d.label ? (
          <text
            key={`xlabel-${i}`}
            x={at(lefts, i, "bar left edge") + (d.labelWidth ?? barWidth) / 2}
            y={height + 6 + (d.labelTextStyle?.fontSize ?? 9)}
            fontSize={d.labelTextStyle?.fontSize ?? 9}
            fill={d.labelTextStyle?.color ?? yAxisTextStyle.color}
            textAnchor="middle"
          >
            {d.label}
          </text>
        ) : null,
      )}
    </svg>
  );
}
